/**
 * What she says, in her own words, about a pregnancy that ended or a birth.
 *
 * The pregnancy-status write has no field for free text
 * (`POST /cycles/{cycleId}/pregnancy-status`), so her words are kept as a
 * patient note linked to the cycle. The note carries a structured marker, the
 * same way the Today completion records do, so the sheet can find her words
 * again when it reopens and the screens that list notes can leave them out.
 *
 * The notes write has no audience to set. Her notes are read only from her own
 * account: a partner's app reads the partner's notes, never hers, so her words
 * stay out of a partner's view just as the kind of loss does.
 */

export const PREGNANCY_DETAILS_NOTE_MARKER = "__pregnancy_details__";
export const PREGNANCY_DETAILS_NOTE_VERSION = 1 as const;

export type PregnancyDetailsNote = {
  [PREGNANCY_DETAILS_NOTE_MARKER]: true;
  version: typeof PREGNANCY_DETAILS_NOTE_VERSION;
  cycleId: string;
  details: string;
  recordedAt: string;
};

type NoteLike = { text: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function parsePregnancyDetailsNote(
  text: string | null | undefined,
): PregnancyDetailsNote | null {
  if (!text) return null;
  try {
    const value: unknown = JSON.parse(text);
    if (!isRecord(value)) return null;
    if (value[PREGNANCY_DETAILS_NOTE_MARKER] !== true) return null;
    if (value.version !== PREGNANCY_DETAILS_NOTE_VERSION) return null;
    if (typeof value.cycleId !== "string" || value.cycleId.length === 0) {
      return null;
    }
    if (
      typeof value.details !== "string" ||
      typeof value.recordedAt !== "string"
    ) {
      return null;
    }
    return {
      [PREGNANCY_DETAILS_NOTE_MARKER]: true,
      version: PREGNANCY_DETAILS_NOTE_VERSION,
      cycleId: value.cycleId,
      details: value.details,
      recordedAt: value.recordedAt,
    };
  } catch {
    return null;
  }
}

/** Whether a note is her pregnancy details rather than a journal entry. */
export function isPregnancyDetailsNote(
  text: string | null | undefined,
): boolean {
  return parsePregnancyDetailsNote(text) !== null;
}

export function serializePregnancyDetailsNote(
  note: Omit<
    PregnancyDetailsNote,
    typeof PREGNANCY_DETAILS_NOTE_MARKER | "version"
  >,
): string {
  return JSON.stringify({
    [PREGNANCY_DETAILS_NOTE_MARKER]: true,
    version: PREGNANCY_DETAILS_NOTE_VERSION,
    ...note,
  } satisfies PregnancyDetailsNote);
}

/** Her latest words about this cycle's pregnancy, and the note holding them. */
export function getPregnancyDetailsNote<T extends NoteLike>(
  notes: readonly T[] | undefined,
  cycleId: string,
): { note: T; record: PregnancyDetailsNote } | null {
  if (!notes || cycleId.length === 0) return null;
  return notes.reduce<{ note: T; record: PregnancyDetailsNote } | null>(
    (latest, note) => {
      const record = parsePregnancyDetailsNote(note.text);
      if (!record || record.cycleId !== cycleId) return latest;
      if (!latest || record.recordedAt > latest.record.recordedAt) {
        return { note, record };
      }
      return latest;
    },
    null,
  );
}
